// Extração por regras da ficha de créditos (.docx). Código puro, compartilhado com o app.
//
// Estrutura esperada: títulos em negrito (código e título do livro, "Ficha de créditos", "LIVRO", nome
// do selo…) e, entre eles, blocos separados por linha em branco em que a primeira linha é a função e as
// seguintes são as pessoas. A função é mantida exatamente como está no arquivo. Os títulos do topo são
// ignorados; um título que aparece depois do primeiro crédito (ex.: "Logos", a equipe do selo) vira seção.

export type CreditRoleCode = "AUTHOR" | "ILLUSTRATOR" | "TRANSLATOR" | "EDITOR" | "NOTES" | "REVIEWER" | "PROOFREADER" | "LAYOUT" | "GRAPHIC_DESIGN" | "OTHER"

/** Uma pessoa com sua função ou, com `roleCode` "SECTION", o título de uma seção (nome em `displayLabel`). */
export interface ExtractedCredit {
  contributorName: string
  roleCode: CreditRoleCode | "SECTION"
  displayLabel: string
}

export interface DocxParagraph {
  text: string
  bold: boolean
  /** Item de lista numerada ou com marcadores no Word. */
  list?: boolean
}

const xmlEntities: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'" }

function decodeXmlEntities(value: string) {
  return value.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, entity: string) => {
    if (entity[0] !== "#") return xmlEntities[entity.toLowerCase()] ?? ""
    const code = entity[1].toLowerCase() === "x" ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10)
    return Number.isFinite(code) ? String.fromCodePoint(code) : ""
  })
}

function isBoldRun(runXml: string) {
  const properties = runXml.match(/<w:rPr>([\s\S]*?)<\/w:rPr>/)?.[1] ?? ""
  const bold = properties.match(/<w:b(?:\s+w:val="([^"]*)")?\s*\/>/)
  return Boolean(bold) && !["0", "false", "off"].includes((bold?.[1] ?? "").toLowerCase())
}

/** Parágrafos do `word/document.xml` com o texto e se todo o texto está em negrito (ou em estilo de título). */
export function docxParagraphs(xml: string): DocxParagraph[] {
  const body = xml.match(/<w:body>([\s\S]*)<\/w:body>/)?.[1] ?? xml
  const paragraphs: DocxParagraph[] = []
  for (const paragraph of body.match(/<w:p[ >][\s\S]*?<\/w:p>|<w:p\/>/g) ?? []) {
    const heading = /<w:pStyle\s+w:val="(?:Heading|Title|Ttulo|Titulo)[^"]*"/i.test(paragraph)
    const runs = (paragraph.match(/<w:r[ >][\s\S]*?<\/w:r>/g) ?? []).map((run) => ({
      text: decodeXmlEntities((run.match(/<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>/g) ?? []).map((part) => part.replace(/<[^>]+>/g, "")).join("")),
      bold: isBoldRun(run),
    }))
    const text = runs.map((run) => run.text).join("").replace(/[ \s]+/g, " ").trim()
    const textRuns = runs.filter((run) => run.text.trim())
    paragraphs.push({ text, bold: Boolean(text) && (heading || (textRuns.length > 0 && textRuns.every((run) => run.bold))), list: /<w:numPr>/.test(paragraph) })
  }
  return paragraphs
}

function normalizeLabel(label: string) {
  return label.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim()
}

const roleRules: Array<[RegExp, CreditRoleCode]> = [
  [/^(autor|autora|autoria|texto)\b/, "AUTHOR"],
  [/^(ilustra(cao|coes|dor|dora))\b/, "ILLUSTRATOR"],
  [/^(tradu(cao|tor|tora))\b/, "TRANSLATOR"],
  [/^(prepara(cao|dor|dora))\b/, "REVIEWER"],
  [/^(revis(ao|or|ora))\b/, "PROOFREADER"],
  [/^(diagrama(cao|dor|dora))\b/, "LAYOUT"],
  [/^projeto grafico\b/, "GRAPHIC_DESIGN"],
  [/^notas?\b/, "NOTES"],
  [/^(edicao|editor|editora)$|^edicao e notas$/, "EDITOR"],
]

/** Código de função do app para o rótulo do arquivo; sem equivalente, "OTHER" (o rótulo do arquivo é mantido). */
export function creditRoleCode(label: string): CreditRoleCode {
  const normalized = normalizeLabel(label)
  return roleRules.find(([pattern]) => pattern.test(normalized))?.[1] ?? "OTHER"
}

/** Créditos na ordem do arquivo: cada pessoa de um bloco recebe a função da primeira linha do bloco. */
export function parseCreditsParagraphs(paragraphs: DocxParagraph[]): ExtractedCredit[] {
  const entries: Array<{ heading: string } | { block: string[] }> = []
  let block: string[] = []
  const closeBlock = () => {
    if (block.length) entries.push({ block })
    block = []
  }
  for (const paragraph of paragraphs) {
    if (!paragraph.text || paragraph.bold) {
      closeBlock()
      if (paragraph.bold) entries.push({ heading: paragraph.text })
      continue
    }
    block.push(paragraph.text)
  }
  closeBlock()

  const credits: ExtractedCredit[] = []
  let pendingSection: string | null = null
  for (const entry of entries) {
    if ("heading" in entry) {
      // Só vira seção o título que vem depois de algum crédito; o do topo é cabeçalho do documento.
      if (credits.length) pendingSection = entry.heading
      continue
    }
    const [label, ...names] = entry.block
    if (!names.length) continue
    if (pendingSection) {
      credits.push({ contributorName: "", roleCode: "SECTION", displayLabel: pendingSection })
      pendingSection = null
    }
    const roleCode = creditRoleCode(label)
    for (const name of names) {
      // A mesma pessoa com duas funções do mesmo código vira um crédito só ("Revisão e Revisão de Prova"):
      // o banco não aceita a mesma pessoa repetida com o mesmo código no livro, nem em seções diferentes.
      const existing = credits.find((credit) => credit.roleCode === roleCode && normalizeLabel(credit.contributorName) === normalizeLabel(name))
      if (existing) existing.displayLabel = `${existing.displayLabel} e ${label}`
      else credits.push({ contributorName: name, roleCode, displayLabel: label })
    }
  }
  return credits
}

export function parseCreditsDocx(xml: string) {
  return parseCreditsParagraphs(docxParagraphs(xml))
}
